export interface ModernFrameNamespaceMetadata {
  /** Unique first URL segment for a canonical modern route. */
  readonly namespace: string;
  /** User-facing namespace label. */
  readonly displayName: string;
  /** Canonical shell redirect for the bare namespace, or null when it must be not-found. */
  readonly rootRedirect: string | null;
}

export interface ModernFramePermissionRegistration {
  /** Namespace that owns these routes. */
  readonly namespace: string;
  /** Permission key passed to the shell page-access API. */
  readonly pagePath: string;
  /** Default canonical route for this permission registration. */
  readonly defaultRoute: string;
  /** User-facing permission-area label. */
  readonly displayName: string;
  /** Canonical routes owned by this permission. `/**` matches a base and descendants. */
  readonly routePatterns: readonly string[];
}

export const MODERN_FRAME_NAMESPACES = [
  {
    namespace: 'work-management',
    displayName: 'Work management',
    rootRedirect: '/work-management/setup/processes',
  },
  { namespace: 'chat', displayName: 'Chat', rootRedirect: null },
  { namespace: 'attachments', displayName: 'Attachments', rootRedirect: null },
  { namespace: 'discussions', displayName: 'Discussions', rootRedirect: null },
  { namespace: 'task-management', displayName: 'Task management', rootRedirect: null },
] as const satisfies readonly ModernFrameNamespaceMetadata[];

export const MODERN_FRAME_PERMISSION_REGISTRATIONS = [
  {
    namespace: 'work-management',
    pagePath: 'work-management/setup',
    defaultRoute: '/work-management/setup/processes',
    displayName: 'Work Management Setup',
    routePatterns: [
      '/work-management/setup/processes',
      '/work-management/setup/processes/:processId/statuses',
      '/work-management/setup/processes/:processId/status-transitions',
    ],
  },
  {
    namespace: 'chat',
    pagePath: 'chat',
    defaultRoute: '/chat',
    displayName: 'Chat',
    routePatterns: ['/chat'],
  },
  {
    namespace: 'attachments',
    pagePath: 'attachments',
    defaultRoute: '/attachments',
    displayName: 'Attachments',
    routePatterns: ['/attachments/**'],
  },
  {
    namespace: 'discussions',
    pagePath: 'discussions',
    defaultRoute: '/discussions',
    displayName: 'Discussions',
    routePatterns: ['/discussions', '/discussions/:discussionId'],
  },
  {
    namespace: 'task-management',
    pagePath: 'task-management/admin-setup',
    defaultRoute: '/task-management/admin-setup/processes',
    displayName: 'Task Management Setup',
    routePatterns: [
      '/task-management/admin-setup/processes',
      '/task-management/admin-setup/processes/:processId/statuses',
      '/task-management/admin-setup/processes/:processId/status-transitions',
    ],
  },
  {
    namespace: 'task-management',
    pagePath: 'task-management/quote-request',
    defaultRoute: '/task-management/quote-request',
    displayName: 'Quote Request',
    routePatterns: [
      '/task-management/quote-request',
      '/task-management/quote-request/details/:taskId',
    ],
  },
  {
    namespace: 'task-management',
    pagePath: 'task-management/product-returns',
    defaultRoute: '/task-management/product-returns',
    displayName: 'Product Returns',
    routePatterns: [
      '/task-management/product-returns',
      '/task-management/product-returns/details/:taskId',
    ],
  },
  {
    namespace: 'task-management',
    pagePath: 'task-management/renewals',
    defaultRoute: '/task-management/renewals',
    displayName: 'Renewals',
    routePatterns: ['/task-management/renewals'],
  },
] as const satisfies readonly ModernFramePermissionRegistration[];

export type ModernFrameNamespaceId = (typeof MODERN_FRAME_NAMESPACES)[number]['namespace'];
export type RegisteredModernFrameNamespace = (typeof MODERN_FRAME_NAMESPACES)[number];
export type RegisteredModernFramePermission =
  (typeof MODERN_FRAME_PERMISSION_REGISTRATIONS)[number];

export const MODERN_FRAME_DEFAULT_NAMESPACE = MODERN_FRAME_NAMESPACES[0]!;
export const MODERN_FRAME_DEFAULT_PERMISSION = MODERN_FRAME_PERMISSION_REGISTRATIONS[0]!;
